import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/App";
import { Star, User, LogOut, Settings, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navigation = ({ currentPage = "" }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const menuItems = [
    { key: "dashboard", label: t('common.dashboard'), path: "/dashboard" },
    { key: "restaurants", label: t('common.restaurants'), path: "/restaurants" },
    { key: "preferences", label: t('common.preferences'), path: "/preferences" },
    { key: "leaderboard", label: t('common.leaderboard'), path: "/leaderboard" },
  ];

  return (
    <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')} data-testid="nav-logo">
            <Star className="w-5 h-5 text-primary fill-primary" />
            <span className="font-semibold gradient-text">Étoile</span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            {menuItems.map(item => (
              <button
                key={item.key}
                onClick={() => navigate(item.path)}
                className={`menu-item text-sm py-2 ${currentPage === item.key ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`nav-${item.key}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 h-10" data-testid="user-menu-trigger">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-popover border-border">
                <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer" data-testid="menu-profile">
                  <Settings className="w-4 h-4 mr-2" />
                  {t('common.profile')}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-muted/50" />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('common.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <div className="flex flex-col gap-2">
              {menuItems.map(item => (
                <button
                  key={item.key}
                  onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                  className={`text-left py-2 ${currentPage === item.key ? 'text-foreground' : 'text-muted-foreground'}`}
                  data-testid={`mobile-nav-${item.key}`}
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}
                className="text-left py-2 text-muted-foreground"
                data-testid="mobile-nav-profile"
              >
                {t('common.profile')}
              </button>
              <button onClick={handleLogout} className="text-left py-2 text-destructive" data-testid="mobile-nav-logout">
                {t('common.logout')}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
